import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { listBookingsForLead, recordBooking, type LeadBooking } from '../lib/api';
import { dateOnly, friendlyError, istToday, rupees } from '../lib/format';
import type { Lead } from '../lib/types';
import { Badge, Button, ErrorText, Modal, SelectField, TextField } from './ui';


// Managers only: the booking on this lead, or a button to record one.
export function BookingPanel({ lead, onChanged }: { lead: Lead; onChanged: () => void }) {
  const [bookings, setBookings] = useState<LeadBooking[]>([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    setBookings(await listBookingsForLead(lead.id).catch(() => []));
  }, [lead.id]);
  useEffect(() => { load(); }, [load]);
  const active = bookings.find((b) => b.status === 'active');
  const closed = lead.stage === 'lost' || lead.stage === 'dormant';
  if (!active && !bookings.length && closed) return null;

  return (
    <section className="border-b border-line px-5 py-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted">Booking and commission</h3>
        {!active && !closed && <Button onClick={() => setOpen(true)} disabled={!lead.project_id}>Record booking</Button>}
      </div>
      {active ? (
        <p className="text-sm">
          <Badge tone="ok">Booked</Badge> {active.unit_label ? `${active.unit_label} · ` : ''}{dateOnly(active.booked_on)} · {rupees(active.agreement_value_inr)} at {Number(active.commission_pct)}%
          {' · '}<Link to="/money" className="text-accent hover:underline">Track the commission</Link>
        </p>
      ) : (
        <p className="text-sm text-muted">{bookings.length ? 'The last booking was cancelled.' : 'When the buyer books, record the unit and agreement value here to start tracking the commission.'}</p>
      )}
      {open && <BookingModal lead={lead} onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await load(); onChanged(); }} />}
    </section>
  );
}

type Split = 'one' | 'two' | 'custom';

function BookingModal({ lead, onClose, onSaved }: { lead: Lead; onClose: () => void; onSaved: () => void }) {
  const { firm } = useAuth();
  const [bookedOn, setBookedOn] = useState(istToday());
  const [unit, setUnit] = useState('');
  const [value, setValue] = useState('');
  const [pct, setPct] = useState('2');
  const [tds, setTds] = useState(String(firm?.default_tds_pct ?? 2));
  const [split, setSplit] = useState<Split>('one');
  const [rows, setRows] = useState([{ label: 'On agreement', share: '50' }, { label: 'On 20% payment', share: '50' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const agreement = Number(value.replace(/[^0-9]/g, '')) || 0;
  const commission = Math.round(agreement * (Number(pct) || 0) / 100);
  const gst = firm?.gst_registered === false ? 0 : Math.round(commission * 0.18);
  const tdsAmt = Math.round(commission * (Number(tds) || 0) / 100);
  const milestones = split === 'one' ? [{ label: 'Full commission', share_pct: 100 }]
    : rows.map((r) => ({ label: r.label, share_pct: Number(r.share) || 0 }));
  const total = milestones.reduce((s, m) => s + m.share_pct, 0);

  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try {
      await recordBooking({ leadId: lead.id, bookedOn, unit, agreementValue: agreement, commissionPct: Number(pct), tdsPct: Number(tds), milestones });
      onSaved();
    } catch (err) { setError(friendlyError(err)); setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title="Record booking" wide>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="Booked on" type="date" required max={istToday()} value={bookedOn} onChange={(e) => setBookedOn(e.target.value)} />
          <TextField label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Tower C, 1203" />
          <TextField label="Agreement value (₹)" required inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value)} placeholder="14200000"
            hint={agreement ? rupees(agreement) : 'The value on the builder-buyer agreement'} />
          <TextField label="Commission rate (%)" required type="number" step="0.001" min="0.001" max="10" value={pct} onChange={(e) => setPct(e.target.value)} />
          <TextField label="TDS the builder deducts (%)" type="number" step="0.01" min="0" max="20" value={tds} onChange={(e) => setTds(e.target.value)}
            hint="Section 194H. 2% unless the builder says otherwise." />
          <SelectField label="How the builder pays" value={split} onChange={(e) => setSplit(e.target.value as Split)}>
            <option value="one">In one payment</option>
            <option value="two">In parts (milestones)</option>
          </SelectField>
        </div>
        {split !== 'one' && (
          <div className="flex flex-col gap-2">
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_110px_auto] items-end gap-2">
                <TextField label={`Milestone ${i + 1}`} required value={r.label} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                <TextField label="Share (%)" required type="number" min="0.001" max="100" step="0.001" value={r.share} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, share: e.target.value } : x))} />
                <Button type="button" variant="ghost" disabled={rows.length <= 2} onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</Button>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <Button type="button" disabled={rows.length >= 6} onClick={() => setRows([...rows, { label: '', share: '' }])}>Add milestone</Button>
              <span className={`text-sm ${total === 100 ? 'text-ok' : 'text-bad'}`}>Total {total}%</span>
            </div>
          </div>
        )}
        {agreement > 0 && (
          <div className="rounded-xl bg-surface2 p-3 text-sm">
            <p className="mb-1 text-xs uppercase tracking-wider text-muted">Preview</p>
            <p>Commission {rupees(commission)} + GST {rupees(gst)} = invoice {rupees(commission + gst)}</p>
            <p>Less TDS {rupees(tdsAmt)} → expected in the bank <strong>{rupees(commission + gst - tdsAmt)}</strong></p>
            {firm?.gst_registered === false && <p className="text-muted">No GST: the firm is set as not GST-registered (Settings).</p>}
          </div>
        )}
        <p className="text-xs text-muted">Recording the booking also moves the lead to Booked.</p>
        <ErrorText>{error}</ErrorText>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy || !agreement || (split !== 'one' && total !== 100)}>{busy ? 'Saving…' : 'Record booking'}</Button>
        </div>
      </form>
    </Modal>
  );
}
